import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    ConfigurationData,
    ConfigurationContextValue,
    NetworkSetupData,
    MappingField,
    ValidationError,
} from '../types';
import { INITIAL_CONFIGURATION } from '../configurationConstants';
import { AppContext } from 'Context/AppContext';
import AuthManager from 'Common/AuthManager';
import { fetchConfiguration, updateConfiguration } from '../Services/configurationService';
import { validateConfiguration, getErrorsByTab } from '../validationUtils';

type ConfigurationProviderProps = {
    children: React.ReactNode;
};

const ConfigurationContext = createContext<ConfigurationContextValue | undefined>(undefined);

const getUserId = (): string => {
    const userObj = AuthManager.getAuthUser();
    return userObj?.profile?.id?.toString() ?? '';
};

const getUserName = (): string => {
    const userObj = AuthManager.getAuthUser();
    return userObj?.profile?.first_name ?? '';
};

export const ConfigurationProvider = ({ children }: ConfigurationProviderProps) => {
    const [originalConfig, setOriginalConfig] = useState<ConfigurationData | null>(null);
    const [currentConfig, setCurrentConfig] = useState<ConfigurationData | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const appData = useContext(AppContext);
    const channelKey = appData?.channelKey;

    useEffect(() => {
        const fetchConfigurationData = async () => {
            if (!channelKey) {
                console.error('ConfigurationContext: No channelKey provided');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            const result = await fetchConfiguration(channelKey);

            if (result.success && result.data) {
                setOriginalConfig(result.data);
                setCurrentConfig(result.data);
            } else {
                setOriginalConfig(INITIAL_CONFIGURATION);
                setCurrentConfig(INITIAL_CONFIGURATION);
            }

            setIsLoading(false);
        };

        fetchConfigurationData();
    }, [channelKey]);

    useEffect(() => {
        if (validationErrors.length > 0 && currentConfig) {
            const validationResult = validateConfiguration(currentConfig);
            setValidationErrors(validationResult.errors);
        }
    }, [currentConfig]);

    const clearValidationErrors = useCallback(() => {
        setValidationErrors([]);
    }, []);

    const updateNetworkSetup = useCallback((data: Partial<NetworkSetupData>) => {
        setCurrentConfig((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                network_setup: {
                    ...prev.network_setup,
                    ...data,
                },
            };
        });
    }, []);

    const updateProductMapping = useCallback((field: MappingField, source: string | null, value: string | null) => {
        setCurrentConfig((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                product: {
                    ...prev.product,
                    [field]: { source: source || '', value: value || '' },
                },
            };
        });
    }, []);

    const updateAvailability = useCallback((availability: string | null) => {
        setCurrentConfig((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                availability: {
                    availability,
                },
            };
        });
    }, []);

    const updateGmsEndpoint = useCallback((endpoint: string | null) => {
        setCurrentConfig((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                gms_endpoint: endpoint,
            };
        });
    }, []);

    const updateLogLocation = useCallback((location: string | null) => {
        setCurrentConfig((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                log_location: location,
            };
        });
    }, []);

    const saveConfiguration = useCallback(async (): Promise<boolean> => {
        if (!currentConfig) return false;

        setValidationErrors([]);

        const validationResult = validateConfiguration(currentConfig);

        if (!validationResult.isValid) {
            setValidationErrors(validationResult.errors);

            const errorsByTab = getErrorsByTab(validationResult.errors);

            const errorMessages: string[] = [];

            if (errorsByTab['Network Setup'].length > 0) {
                errorMessages.push(`Network Setup: ${errorsByTab['Network Setup'].length} error(s)`);
            }
            if (errorsByTab['Products'].length > 0) {
                errorMessages.push(`Products: ${errorsByTab['Products'].length} error(s)`);
            }
            if (errorsByTab['Availability'].length > 0) {
                errorMessages.push(`Availability: ${errorsByTab['Availability'].length} error(s)`);
            }

            toast.error(
                <div>
                    <div className="tw-font-semibold">Please fix the following errors:</div>
                    <ul className="tw-mt-1 tw-ml-4 tw-list-disc">
                        {errorMessages.map((msg, index) => (
                            <li key={index}>{msg}</li>
                        ))}
                    </ul>
                </div>
            );

            return false;
        }

        const userId = getUserId();
        const username = getUserName();

        if (!userId || !username) {
            toast.error('User information not available. Please login again.');
            return false;
        }

        setIsSaving(true);

        const payload: ConfigurationData = {
            ...currentConfig,
            channel_key: channelKey,
            updated_by_user_id: userId,
            updated_by_username: username,
        };

        const result = await updateConfiguration(channelKey, payload);

        if (result.success) {
            toast.success('Saved Successfully');
            setOriginalConfig(currentConfig);
            setIsEditMode(false);
            setIsSaving(false);
            return true;
        } else {
            toast.error(result.error || 'Failed to save configuration');
            setIsSaving(false);
            return false;
        }
    }, [channelKey, currentConfig]);

    const cancelEdit = useCallback(() => {
        setCurrentConfig(originalConfig);
        setValidationErrors([]);
        setIsEditMode(false);
    }, [originalConfig]);

    const enableEditMode = useCallback(() => {
        setIsEditMode(true);
    }, []);

    const value: ConfigurationContextValue = {
        originalConfig,
        currentConfig,
        isEditMode,
        isSaving,
        isLoading,
        validationErrors,
        setIsEditMode,
        updateNetworkSetup,
        updateProductMapping,
        updateAvailability,
        updateGmsEndpoint,
        updateLogLocation,
        saveConfiguration,
        cancelEdit,
        enableEditMode,
        clearValidationErrors,
    };

    return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

export const useConfigurationContext = () => {
    const context = useContext(ConfigurationContext);
    if (context === undefined) {
        throw new Error('useConfigurationContext must be used within ConfigurationProvider');
    }
    return context;
};